import {BrowserModule} from '@angular/platform-browser';
import {NgModule} from '@angular/core';
import {HttpClientModule, HTTP_INTERCEPTORS} from '@angular/common/http';
import {AuthInterceptor} from './interceptors/auth.interceptor';

import {AppRoutingModule} from '@/app-routing.module';
import {AppComponent} from './app.component';
import {MainComponent} from '@modules/main/main.component';
import {LoginComponent} from '@modules/login/login.component';
import {HeaderComponent} from '@modules/main/header/header.component';
import {FooterComponent} from '@modules/main/footer/footer.component';
import {MenuSidebarComponent} from '@modules/main/menu-sidebar/menu-sidebar.component';
import {BlankComponent} from '@pages/blank/blank.component';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {ProfileComponent} from '@pages/profile/profile.component';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {RegisterComponent} from '@modules/register/register.component';
import {DashboardComponent} from '@pages/dashboard/dashboard.component';
import {ToastrModule} from 'ngx-toastr';
import {MessagesComponent} from '@modules/main/header/messages/messages.component';
import {NotificationsComponent} from '@modules/main/header/notifications/notifications.component';

import {registerLocaleData} from '@angular/common';
import localeEn from '@angular/common/locales/en';
import {UserComponent} from '@modules/main/header/user/user.component';
import {ForgotPasswordComponent} from '@modules/forgot-password/forgot-password.component';
import {RecoverPasswordComponent} from '@modules/recover-password/recover-password.component';
import {LanguageComponent} from '@modules/main/header/language/language.component';
import {MainMenuComponent} from './pages/main-menu/main-menu.component';
import {SubMenuComponent} from './pages/main-menu/sub-menu/sub-menu.component';
import {MenuItemComponent} from './components/menu-item/menu-item.component';
import {ControlSidebarComponent} from './modules/main/control-sidebar/control-sidebar.component';
import {StoreModule} from '@ngrx/store';
import {authReducer} from './store/auth/reducer';
import {uiReducer} from './store/ui/reducer';
import {ProfabricComponentsModule} from '@profabric/angular-components';
import {defineCustomElements} from '@profabric/web-components/loader';
import {SidebarSearchComponent} from './components/sidebar-search/sidebar-search.component';
import { CoursesComponent } from '@pages/courses/courses.component';
import { CoursedetailComponent } from '@pages/courses/coursedetail/coursedetail.component';
import { LeveldetailComponent } from '@pages/courses/coursedetail/leveldetail/leveldetail.component';
import { PhasedetailComponent } from '@pages/courses/coursedetail/leveldetail/phasedetail/phasedetail.component';
import { KeywordComponent } from '@pages/courses/coursedetail/leveldetail/phasedetail/keyword/keyword.component';
import { NgxDropzoneModule } from 'ngx-dropzone';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NewlanguageComponent } from '@pages/blank/newlanguage/newlanguage.component';
import { NewcourseComponent } from '@pages/courses/newcourse/newcourse.component';
import { NewlevelComponent } from '@pages/courses/coursedetail/newlevel/newlevel.component';
import { ColorPickerModule } from 'ngx-color-picker';
import { NewphaseComponent } from '@pages/courses/coursedetail/leveldetail/newphase/newphase.component';
import { NewkeywordComponent } from '@pages/courses/coursedetail/leveldetail/phasedetail/newkeyword/newkeyword.component';
import { AvatarComponent } from '@pages/avatar/avatar.component';
import { NewavatarComponent } from '@pages/avatar/newavatar/newavatar.component';
import { KeywordtypeComponent } from '@pages/courses/coursedetail/keywordtype/keywordtype.component';
import { SocialCardComponent } from '@pages/courses/coursedetail/leveldetail/phasedetail/keyword/social-card/social-card.component';
import { BlogEditComponent } from '@pages/blog/blog-edit/blog-edit.component';
import { BlogListComponent } from '@pages/blog/blog-list/blog-list.component';
import { BlogCategoryManageComponent } from '@pages/blog/blog-category-manage/blog-category-manage.component';
import { QuillModule } from 'ngx-quill';
import { UsersComponent } from '@pages/users/users.component';
import { BadgeListComponent } from '@pages/badges/badge-list/badge-list.component';
import { BadgeEditComponent } from '@pages/badges/badge-edit/badge-edit.component';
import { BlogModule } from '@pages/blog/blog.module';
import { PromptModule } from '@pages/prompt/prompt.module';
import { GeminiBatchJobsModule } from '@pages/gemini-batch-jobs/gemini-batch-jobs.module';
import { NotificationsPageModule } from '@pages/notifications/notifications.module';
import { PremiumPageModule } from '@pages/premium/premium.module';

defineCustomElements();
registerLocaleData(localeEn, 'en-EN');

@NgModule({
    declarations: [
        AppComponent,
        MainComponent,
        LoginComponent,
        HeaderComponent,
        FooterComponent,
        MenuSidebarComponent,
        BlankComponent,
        ProfileComponent,
        RegisterComponent,
        DashboardComponent,
        MessagesComponent,
        NotificationsComponent,
        UserComponent,
        ForgotPasswordComponent,
        RecoverPasswordComponent,
        LanguageComponent,
        MainMenuComponent,
        SubMenuComponent,
        MenuItemComponent,
        ControlSidebarComponent,
        SidebarSearchComponent,
        CoursesComponent,
        CoursedetailComponent,
        LeveldetailComponent,
        PhasedetailComponent,
        KeywordComponent,
        NewlanguageComponent,
        NewcourseComponent,
        NewlevelComponent,
        NewphaseComponent,
        NewkeywordComponent,
        AvatarComponent,
        NewavatarComponent,
        KeywordtypeComponent,
        SocialCardComponent,

        UsersComponent,
        BadgeListComponent,
        BadgeEditComponent
    ],
    imports: [
        BrowserModule,
        StoreModule.forRoot({auth: authReducer, ui: uiReducer}),
        HttpClientModule,
        AppRoutingModule,
        FormsModule,
        ReactiveFormsModule,
        BrowserAnimationsModule,
      NgbModule,
        ToastrModule.forRoot({
            timeOut: 3000,
            positionClass: 'toast-top-right',
            preventDuplicates: true
        }),
        ProfabricComponentsModule,
        NgxDropzoneModule,
        ColorPickerModule,
        QuillModule.forRoot({
            modules: {
                toolbar: [
                    ['bold', 'italic', 'underline', 'strike'],        // toggled buttons
                    ['blockquote', 'code-block'],

                    [{ 'header': 1 }, { 'header': 2 }],               // custom button values
                    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                    [{ 'script': 'sub'}, { 'script': 'super' }],      // superscript/subscript
                    [{ 'indent': '-1'}, { 'indent': '+1' }],          // outdent/indent
                    [{ 'direction': 'rtl' }],                         // text direction

                    [{ 'size': ['small', false, 'large', 'huge'] }],  // custom dropdown
                    [{ 'header': [1, 2, 3, 4, 5, 6, false] }],

                    [{ 'color': [] }, { 'background': [] }],          // dropdown with defaults from theme
                    [{ 'font': [] }],
                    [{ 'align': [] }],

                    ['clean'],                                         // remove formatting button

                    ['link', 'image', 'video']                         // link and image, video
                ]
            }
        }),
        BlogModule,
        PromptModule,
        GeminiBatchJobsModule,
        NotificationsPageModule,
        PremiumPageModule
    ],
    providers: [
        {provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true}
    ],
    entryComponents:[NewlanguageComponent,
        NewcourseComponent,
        NewlevelComponent,
        NewphaseComponent,
        NewkeywordComponent,
        NewavatarComponent,
    KeywordtypeComponent],
    bootstrap: [AppComponent]
})
export class AppModule {}
